import type {
  BetMove,
  CardKind,
  HandRank,
  LoveWinsAllPlayerId,
  LoveWinsAllState,
  LoveWinsAllViewerState,
  RankInfo,
  RpsKind,
} from "../types";
import {
  CARD_KINDS,
  DECK_COMPOSITION,
  HAND_SIZE,
  LOVE_WINS_ALL_BET_UNIT,
  LOVE_WINS_ALL_PLAYERS,
  LOVE_WINS_ALL_RAISE_CAP,
  RPS_KINDS,
} from "../types";

export function isPlayerId(value: unknown): value is LoveWinsAllPlayerId {
  return (
    typeof value === "string" &&
    LOVE_WINS_ALL_PLAYERS.includes(value as LoveWinsAllPlayerId)
  );
}

export function getOpponentPlayerId(
  playerId: LoveWinsAllPlayerId,
): LoveWinsAllPlayerId {
  return playerId === "p1" ? "p2" : "p1";
}

// ---- Deterministic PRNG (mulberry32) + deck ---------------------------------
/** Pure mulberry32 step: returns a value in [0,1) and the next state. */
export function nextRng(state: number): { value: number; state: number } {
  const a = (state + 0x6d2b79f5) | 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  const value = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  return { value, state: a };
}

/** The full 30-card deck (unshuffled). */
export function fullDeck(): CardKind[] {
  const deck: CardKind[] = [];
  for (const kind of CARD_KINDS) {
    for (let i = 0; i < DECK_COMPOSITION[kind]; i += 1) deck.push(kind);
  }
  return deck;
}

/** Fisher–Yates shuffle of a copy of `deck` using the seeded PRNG. */
export function shuffleDeck(
  deck: readonly CardKind[],
  rngState: number,
): { deck: CardKind[]; state: number } {
  const arr = [...deck];
  let state = rngState;
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const step = nextRng(state);
    state = step.state;
    const j = Math.floor(step.value * (i + 1));
    const swapped = arr[i] as CardKind;
    arr[i] = arr[j] as CardKind;
    arr[j] = swapped;
  }
  return { deck: arr, state };
}

// ---- Hand ranking (족보) -----------------------------------------------------
const TIER: Record<HandRank, number> = { three: 3, two: 2, one: 1 };
const BEATS: Record<RpsKind, RpsKind> = {
  scissors: "paper",
  paper: "rock",
  rock: "scissors",
};

/**
 * Classify a 3-card hand. Love is wild — it fills to maximize the tier, and
 * among kind ties uses the RPS_KINDS priority order (scissors > rock > paper).
 */
export function classifyHand(hand: readonly CardKind[]): RankInfo {
  let loveCount = 0;
  const counts: Record<RpsKind, number> = { scissors: 0, rock: 0, paper: 0 };
  for (const card of hand) {
    if (card === "love") loveCount += 1;
    else counts[card] += 1;
  }
  if (loveCount === HAND_SIZE) {
    return { rank: "three", kind: null, kicker: null, isLoveTriple: true };
  }
  let maxKind: RpsKind = RPS_KINDS[0];
  for (const kind of RPS_KINDS) {
    if (counts[kind] > counts[maxKind]) maxKind = kind;
  }
  const maxWithLove = counts[maxKind] + loveCount;
  if (maxWithLove >= 3) {
    return { rank: "three", kind: maxKind, kicker: null, isLoveTriple: false };
  }
  if (maxWithLove >= 2) {
    const kicker =
      RPS_KINDS.find((kind) => kind !== maxKind && counts[kind] > 0) ?? null;
    return { rank: "two", kind: maxKind, kicker, isLoveTriple: false };
  }
  return { rank: "one", kind: null, kicker: null, isLoveTriple: false };
}

/** RPS head-to-head: 1 = x wins, -1 = y wins, 0 = tie. null is weakest. */
export function rpsCompare(x: RpsKind | null, y: RpsKind | null): number {
  if (x === y) return 0;
  if (x === null) return -1;
  if (y === null) return 1;
  if (BEATS[x] === y) return 1;
  if (BEATS[y] === x) return -1;
  return 0;
}

/** Head-to-head rank comparison: 1 = a wins, -1 = b wins, 0 = draw. */
export function compareRankInfo(a: RankInfo, b: RankInfo): number {
  if (TIER[a.rank] !== TIER[b.rank]) {
    return Math.sign(TIER[a.rank] - TIER[b.rank]);
  }
  if (a.rank === "one") return 0;
  if (a.rank === "three") {
    if (a.isLoveTriple && !b.isLoveTriple) return 1;
    if (b.isLoveTriple && !a.isLoveTriple) return -1;
    if (a.isLoveTriple && b.isLoveTriple) return 0;
    return rpsCompare(a.kind, b.kind);
  }
  const pairCompare = rpsCompare(a.kind, b.kind);
  if (pairCompare !== 0) return pairCompare;
  return rpsCompare(a.kicker, b.kicker);
}

export function compareHands(
  a: readonly CardKind[],
  b: readonly CardKind[],
): number {
  return compareRankInfo(classifyHand(a), classifyHand(b));
}

export function handRank(hand: readonly CardKind[]): HandRank {
  return classifyHand(hand).rank;
}

// ---- Betting ----------------------------------------------------------------
/** Chips the acting player must add to match the opponent (0 if none). */
export function outstandingToCall(state: LoveWinsAllState): number {
  const opponent = getOpponentPlayerId(state.currentPlayerId);
  return Math.max(
    0,
    state.contributed[opponent] - state.contributed[state.currentPlayerId],
  );
}

/** Legal betting moves for the current actor. */
export function legalBetMoves(state: LoveWinsAllState): readonly BetMove[] {
  if (state.phase !== "bet") return [];
  const actor = state.currentPlayerId;
  const chips = state.chips[actor];
  const toCall = outstandingToCall(state);

  if (toCall > 0) {
    const moves: BetMove[] = ["fold"];
    if (chips >= toCall) moves.push("call");
    if (
      state.raisesUsed < LOVE_WINS_ALL_RAISE_CAP &&
      chips >= toCall + LOVE_WINS_ALL_BET_UNIT
    ) {
      moves.push("raise");
    }
    return moves;
  }
  const moves: BetMove[] = ["check"];
  if (state.lastAggressorId === null && chips >= LOVE_WINS_ALL_BET_UNIT) {
    moves.push("bet");
  }
  return moves;
}

/** Round winner by hand rank, or "push" on a draw. */
export function compareRoundHands(
  state: LoveWinsAllState,
): LoveWinsAllPlayerId | "push" {
  const result = compareHands(state.hands.p1, state.hands.p2);
  if (result > 0) return "p1";
  if (result < 0) return "p2";
  return "push";
}

/** A player at 0 chips loses immediately. Returns the winner, else null. */
export function chipWinner(
  chips: Readonly<Record<LoveWinsAllPlayerId, number>>,
): LoveWinsAllPlayerId | null {
  if (chips.p1 <= 0 && chips.p2 <= 0) return null;
  if (chips.p1 <= 0) return "p2";
  if (chips.p2 <= 0) return "p1";
  return null;
}

// ---- Hidden-info projection -------------------------------------------------
/**
 * Symmetric viewer projection. A viewer sees their OWN hand; the opponent's
 * hand is reduced to a count (cards masked). The deck and PRNG state are dropped
 * entirely — they never reach the client. A null viewer (observer) sees both
 * hands masked. `lastRound` (settled) is public. Built field-by-field so no
 * server-only data leaks via object spread.
 */
export function filterStateForViewer(
  state: LoveWinsAllState,
  viewerPlayerId: string | null,
): LoveWinsAllViewerState {
  const projectHand = (owner: LoveWinsAllPlayerId) => {
    const hand = state.hands[owner];
    return {
      count: hand.length,
      cards: viewerPlayerId === owner ? hand : null,
    };
  };
  return {
    gameId: state.gameId,
    players: state.players,
    phase: state.phase,
    round: state.round,
    leadPlayerId: state.leadPlayerId,
    currentPlayerId: state.currentPlayerId,
    hands: { p1: projectHand("p1"), p2: projectHand("p2") },
    chips: state.chips,
    pot: state.pot,
    contributed: state.contributed,
    raisesUsed: state.raisesUsed,
    lastAggressorId: state.lastAggressorId,
    lastRound: state.lastRound,
    winnerId: state.winnerId,
    lastAction: state.lastAction,
    moveNumber: state.moveNumber,
  };
}

export function buildViewerStates(
  state: LoveWinsAllState,
  observerUserIds: readonly string[] = [],
): Readonly<Record<string, LoveWinsAllViewerState>> {
  const playerEntries = state.players.map(
    (player) =>
      [player.playerId, filterStateForViewer(state, player.playerId)] as const,
  );
  const observerEntries = [...new Set(observerUserIds)].map(
    (observerUserId) =>
      [observerUserId, filterStateForViewer(state, null)] as const,
  );
  return Object.fromEntries([...playerEntries, ...observerEntries]);
}
